import { Box, Checkbox as MuiCheckbox, Stack, SxProps, Theme, Typography } from "@mui/material";
import xor from "lodash/xor";
import {
  Children,
  createContext,
  MouseEvent,
  ReactElement,
  ReactNode,
  useContext,
  useId,
  useMemo,
} from "react";
import { useTranslation } from "../../../i18n";
import { Button } from "../../Inputs/Button/Button";

/**
 * The state a row is in.
 *
 * - `default` — selectable.
 * - `disabled` — shown but not selectable, with the reason in `secondary` or
 *   `note`.
 * - `locked` — the row's outcome has settled, so it reads as checked whether or
 *   not it is in `value`. A second submit therefore re-sends only what has not
 *   succeeded.
 *
 * Both non-selectable states share a cursor and read as inert the same way —
 * the name and the trailing slot fade, and the checkbox says which of the two it
 * is — because **the reason stays readable**: a row's `secondary` and `note`
 * carry why it is unavailable or what outcome it reached, so nothing may dim
 * them. That rules out an opacity wash over the row.
 */
export const srSelectionListRowStates = ["default", "disabled", "locked"] as const;
export type SrSelectionListRowState = (typeof srSelectionListRowStates)[number];

/** Does a slot render anything? `0` does; `""`, `[]` and an all-nullish array do not. */
const isFilled = (node: ReactNode) => node !== "" && Children.toArray(node).length > 0;

type SrSelectionListContextValue = Readonly<{
  value: readonly string[];
  onChange(value: readonly string[]): void;
}>;

const Context = createContext<SrSelectionListContextValue | null>(null);

const useSelectionContext = (component: string): SrSelectionListContextValue => {
  const context = useContext(Context);
  if (!context) {
    throw new Error(`${component} must be used within an SrSelectionList`);
  }
  return context;
};

export type SrSelectionListProps = Readonly<{
  /** The selected row values. Selection spans sections, so it lives here. */
  value: readonly string[];
  onChange(value: readonly string[]): void;
  children: ReactNode;
  sx?: SxProps<Theme>;
}>;

/**
 * **SrSelectionList** is a list of selectable rows, grouped into sections that
 * each carry their own select-all affordance.
 *
 * Selection is controlled here, as one set of row values across every section,
 * so rows and sections do the set arithmetic rather than each caller repeating
 * it. Content is slot-based, and the only copy the list owns is the select-all
 * toggle's, which comes from the kit's own catalogue in `src/i18n`.
 *
 * - `SrSelectionList.Section` — a named group of rows with its own select-all.
 * - `SrSelectionList.Row` — one selectable row.
 */
export const SrSelectionList = ({
  value,
  onChange,
  children,
  sx,
}: SrSelectionListProps): ReactElement => {
  const context = useMemo(() => ({ value, onChange }), [value, onChange]);

  return (
    <Context.Provider value={context}>
      <Stack sx={[{ gap: 1.25 }, ...(Array.isArray(sx) ? sx : [sx])]}>{children}</Stack>
    </Context.Provider>
  );
};

export type SrSelectionListSectionProps = Readonly<{
  /** Heading above the section bar, for a section that starts a new group. */
  heading?: string;
  /** Leading slot in the section bar — where the group names itself. */
  label?: ReactNode;
  /** Shows the select-all / clear-all toggle. Requires `selectableValues`. */
  enableSelectAll?: boolean;
  /**
   * The values the select-all covers — this section's selectable rows, so leave
   * out anything `disabled` or `locked`. Named here rather than read off the
   * rendered rows, so the toggle's wording is right on first paint.
   */
  selectableValues?: readonly string[];
  /** Footnote under the list, e.g. a "3 more not shown" line. */
  footnote?: string;
  children: ReactNode;
  sx?: SxProps<Theme>;
}>;

const SrSelectionListSection = ({
  heading,
  label,
  enableSelectAll = true,
  selectableValues = [],
  footnote,
  children,
  sx,
}: SrSelectionListSectionProps): ReactElement => {
  const { value, onChange } = useSelectionContext("SrSelectionList.Section");
  const { t } = useTranslation("srSelectionList");
  const headingId = useId();
  const labelId = useId();
  const hasHeading = isFilled(heading);
  const hasLabel = isFilled(label);
  // An empty cover would render a toggle that cannot do anything.
  const hasToggleAll = enableSelectAll && selectableValues.length > 0;
  const isAllSelected = selectableValues.every((candidate) => value.includes(candidate));

  const onToggleAll = () =>
    onChange(
      isAllSelected
        ? value.filter((candidate) => !selectableValues.includes(candidate))
        : [...new Set([...value, ...selectableValues])],
    );

  const namingId = hasHeading ? headingId : labelId;
  const isNamed = hasHeading || hasLabel;

  return (
    // Named as a group rather than naming the list inside it: every section's
    // toggle reads the same "Select all", so out-of-context navigation (Tab, a
    // rotor's button list) needs the surrounding group to tell them apart.
    <Box
      role="group"
      aria-labelledby={isNamed ? namingId : undefined}
      sx={[
        { display: "flex", flexDirection: "column", gap: 0.75 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {hasHeading ? (
        <Typography id={headingId} variant="overline" component="h6" sx={{ pt: 1.5 }}>
          {heading}
        </Typography>
      ) : null}
      {hasLabel || hasToggleAll ? (
        // The toggle comes and goes, and the bar may not change height when it
        // does, so it reserves the themed `small` button's 30px.
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minHeight: 30 }}>
          {hasLabel ? (
            <Box id={labelId} sx={{ minWidth: 0 }}>
              {label}
            </Box>
          ) : null}
          {hasToggleAll ? (
            <Button
              variant="text"
              size="small"
              onClick={onToggleAll}
              sx={{ ml: "auto", minWidth: 0, px: 0.5, py: 0 }}
            >
              {isAllSelected ? t("clearAll") : t("selectAll")}
            </Button>
          ) : null}
        </Box>
      ) : null}
      <Box
        component="ul"
        sx={(theme) => ({
          listStyle: "none",
          m: 0,
          p: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: "1px solid",
          borderColor: "stroke.main",
          borderRadius: `${theme.shape.borderRadiusSm}px`,
          backgroundColor: theme.vars.palette.surface.main,
        })}
      >
        {children}
      </Box>
      {isFilled(footnote) ? (
        <Typography variant="caption" sx={{ pl: 1.5, color: "text.secondary" }}>
          {footnote}
        </Typography>
      ) : null}
    </Box>
  );
};

SrSelectionListSection.displayName = "SrSelectionListSection";

SrSelectionList.Section = SrSelectionListSection;

export type SrSelectionListRowProps = Readonly<{
  /** This row's identity within the list's `value`. */
  value: string;
  state?: SrSelectionListRowState;
  /** Primary text. It names the row's checkbox, so keep it identifying. */
  primary: ReactNode;
  /** Metadata line under the primary text. */
  secondary?: ReactNode;
  /** Trailing slot on the primary line, pushed to the end of the row. */
  trailing?: ReactNode;
  /** Note under the metadata line, across the full width of the text column. */
  note?: ReactNode;
  sx?: SxProps<Theme>;
}>;

// The row body is a click target on top of the checkbox, which stays the only
// focusable control. Anything that already handled the activation must not
// toggle a second time — including the checkbox itself, whose click (and the one
// Space synthesizes) bubbles to the row, which would fire `onChange` twice per
// press.
const INTERACTIVE_DESCENDANT_SELECTOR =
  'a,button,input,select,textarea,[role="button"],[role="link"]';

// The only dimming — the name keeps `text.primary`, and a dimmer token under
// this took it to 2.1:1. The checkbox gets its own 0.5 from the theme.
const INERT_OPACITY = 0.5;

const SrSelectionListRow = ({
  value: rowValue,
  state = "default",
  primary,
  secondary,
  trailing,
  note,
  sx,
}: SrSelectionListRowProps): ReactElement => {
  const { value, onChange } = useSelectionContext("SrSelectionList.Row");
  const primaryId = useId();
  const isSelectable = state === "default";
  // A settled row reads as checked whether or not it is in `value`, so a
  // re-submit can leave it out without the row appearing to come undone.
  const isChecked = state === "locked" || value.includes(rowValue);

  const onToggle = () => onChange(xor([...value], [rowValue]));

  const onRowClick = (event: MouseEvent<HTMLElement>) => {
    if (!isSelectable) return;
    if ((event.target as HTMLElement).closest(INTERACTIVE_DESCENDANT_SELECTOR)) return;
    onToggle();
  };

  return (
    <Box
      component="li"
      data-state={state}
      onClick={onRowClick}
      sx={[
        (theme) => ({
          // Grid, so the checkbox's cell is the primary line's own height and
          // centring in it lands the box on the text it names.
          display: "grid",
          gridTemplateColumns: "auto minmax(0, 1fr)",
          columnGap: 1.25,
          rowGap: 0.25,
          px: 1.5,
          py: 1,
          userSelect: "none",
          transition: theme.transitions.create("background-color"),
          "&:not(:last-of-type)": { borderBottom: `1px solid ${theme.vars.palette.stroke.main}` },
          ...(isSelectable
            ? {
                cursor: "pointer",
                "&:hover": { backgroundColor: theme.vars.palette.surface.overlay },
              }
            : {
                // A disabled checkbox has `pointer-events: none`, so hovering the
                // most clickable-looking part of the row falls through to here —
                // which is exactly where "there is nothing to click" belongs.
                cursor: "not-allowed",
                // "the reason stays readable" — a dimmed surface, not an
                // opacity wash over the row. Which of the two states this is
                // stays the checkbox's to say; the surface is the same either
                // way.
                backgroundColor: theme.vars.palette.surface.dim,
              }),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <MuiCheckbox
        checked={isChecked}
        disabled={!isSelectable}
        onChange={onToggle}
        inputProps={{ "aria-labelledby": primaryId }}
        // The theme gives a standalone checkbox a 5px margin; in a row the gap
        // is the row's to set.
        sx={{ m: 0, alignSelf: "center" }}
      />
      <Box sx={{ gridColumn: 2, display: "flex", alignItems: "center", gap: 1.25 }}>
        <Typography
          id={primaryId}
          variant="h6"
          component="span"
          sx={{
            minWidth: 0,
            color: "text.primary",
            ...(isSelectable ? {} : { opacity: INERT_OPACITY }),
          }}
        >
          {primary}
        </Typography>
        {isFilled(trailing) ? (
          <Box
            sx={{
              ml: "auto",
              flex: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              ...(isSelectable ? {} : { opacity: INERT_OPACITY }),
            }}
          >
            {trailing}
          </Box>
        ) : null}
      </Box>
      {isFilled(secondary) ? (
        <Typography
          variant="caption"
          component="div"
          sx={{
            gridColumn: 2,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.75,
            color: "text.secondary",
          }}
        >
          {secondary}
        </Typography>
      ) : null}
      {isFilled(note) ? (
        <Typography
          variant="caption"
          component="div"
          sx={{ gridColumn: 2, color: "text.secondary" }}
        >
          {note}
        </Typography>
      ) : null}
    </Box>
  );
};

SrSelectionListRow.displayName = "SrSelectionListRow";

SrSelectionList.Row = SrSelectionListRow;

SrSelectionList.displayName = "SrSelectionList";
